import promotionsServices from 'src/services/promotions/axios/promotionsServices';
import {
  IPromotionsResponse,
  IPromotionDetailsResponse,
  INewPromotionResponse,
  IStoresPromotionResponse
} from 'src/interfaces/promotions';
import { IPaginationRequest, IServiceParams } from 'src/interfaces/services';
import { useMutation, useQuery } from 'react-query';
import promotionsCalendarService from 'src/services/promotions/calendar/promotionsCalendarService';
import { IPromotionsCalendarResponse } from 'src/interfaces/calendar';
import { ILiquidationFileInfo, ILiquidationSummary } from 'src/interfaces/sharedContribution';
import { downloadPreSignedURL } from 'src/utils/files';

export const useGetPromotions = (serviceParams: IServiceParams) => {
  const query = useQuery<IPromotionsResponse, Error>(
    ['find-all-promotions', serviceParams.pagination],
    async () => {
      const promotions = await promotionsServices.findAll(serviceParams);
      return promotions;
    },
    {
      enabled: true,
      retry: 1
    }
  );
  return query;
};

export const useGetPromotionDetails = (
  promoId: string,
  companyId: string,
  pagination: IPaginationRequest
) => {
  const query = useQuery<IPromotionDetailsResponse, Error>(
    ['find-promotion-details', pagination],
    async () => {
      const promotions = await promotionsServices.findDetails(promoId, companyId, pagination);
      return promotions;
    },
    {
      enabled: true,
      retry: 1
    }
  );
  return query;
};

export const useGetCalendarPromotions = (startDate: string, stopDate: string, cuits: string[]) => {
  return useQuery<IPromotionsCalendarResponse, Error>(
    ['getCalendarPromos', startDate, stopDate],
    async () => {
      return await promotionsCalendarService.getCalendarPromos(startDate, stopDate, cuits);
    },
    {
      enabled: true,
      retry: 1
    }
  );
};

export const usePostNewPromotions = (promotionTitle: string) => {
  return useQuery<INewPromotionResponse, Error>(
    ['create'],
    async () => {
      return await promotionsServices.create(promotionTitle);
    },
    {
      enabled: true,
      retry: 1
    }
  );
};

export const useGetStoresPromotions = (cuits: string[], pagination: IPaginationRequest) => {
  return useQuery<IStoresPromotionResponse, Error>(
    ['getStoresPromos', cuits, pagination],
    async () => {
      return await promotionsServices.getStoresPromos(cuits, pagination);
    },
    {
      enabled: true,
      retry: 1
    }
  );
};

export const useGetLiquidationSummary = (fileId: string, companyId: string) => {
  return useQuery<ILiquidationSummary, Error>(
    [`getLiquidationSummary-${fileId}`, fileId, companyId],
    async () => {
      return await promotionsServices.getLiquidationSummary(fileId, companyId);
    },
    {
      enabled: true,
      retry: 1
    }
  );
};

export const useGetLiquidationFile = (fileId: string, companyId: string, onError, onSuccess) => {
  return useMutation<ILiquidationFileInfo, Error>(
    [`getLiquidationSummary-${fileId}`, fileId, companyId],
    async () => {
      return await promotionsServices.getLiquidationFile(fileId, companyId);
    },
    {
      onSuccess: (response) => {
        downloadPreSignedURL(response?.url);
        onSuccess()
      },
      onError
    }
  );
};
